import { DISPLAY_CURRENCIES, MOBILE_SEARCH } from "common/constants";
import { useSelector } from "react-redux";
import {
  selectPortfolioDataLoadingStates,
  selectPortfolioPrecision,
} from "@shared/slices/portfolio.slice";
import React, { useCallback, useContext, useMemo } from "react";
import AccountsTable from "features/portfolio/Accounts/AccountsTable";
import AccountsCardsList from "features/portfolio/Accounts/AccountsCardList";
import { getAccountExtraDetailsRoute } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { ConsolidatedAccountData, PrecisionType } from "@shared/types";
import AccountsEmpty from "features/portfolio/Accounts/AccountsEmpty";
import { portfolioFilters } from "@shared/slices/filters.slice";
import { AccountsPageContext } from "features/portfolio/Accounts/AccountsPage.context.ts";
import { useClientsData } from "@shared/utils/hooks/misc";
import { useBottomNavigator } from "utils/hooks/misc";
import useMedia from "utils/hooks/useMedia.ts";
import { useCreateAccountClick } from "features/portfolio/common/hooks.tsx";

interface AccountsTableContentProps {
  searchFilter?: string;
  renderTopContainer?: () => React.JSX.Element;
}

const AccountsTableContent = ({
  searchFilter,
  renderTopContainer,
}: AccountsTableContentProps) => {
  const { accounts, isLoadingPortfolioData, isLoading, isCardsView } =
    useContext(AccountsPageContext)!;
  const { historyPush } = useHistoryWithQuery();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { loaded } = useSelector(selectPortfolioDataLoadingStates);
  const { notDeletedClientsData } = useClientsData();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { onCreateAccountClick, whopModal } = useCreateAccountClick();

  const filteredData = useMemo(
    () =>
      accounts
        .filter((accountData) =>
          accountData.client
            .toLowerCase()
            .includes((searchFilter || "").toLowerCase())
        )
        .map((accountData) => ({
          ...accountData,
          key: accountData.client_id,
        })),
    [accounts, searchFilter]
  );

  const totalHeader =
    displayCurrency === DISPLAY_CURRENCIES.BTC
      ? "total_in_btc"
      : "total_in_usd";
  const availableHeader =
    displayCurrency === DISPLAY_CURRENCIES.BTC
      ? "available_in_btc"
      : "available_in_usd";

  const getTotalAmount = (accountData: ConsolidatedAccountData) => (
    <TextWithPrecisionAndIcon
      precisionSelector={selectPortfolioPrecision}
      precisionType={PrecisionType.AMOUNT}
      text={accountData[totalHeader]}
      round={true}
    />
  );

  const openAccountExtraDetails = useCallback(
    (accountData: ConsolidatedAccountData) => {
      historyPush(
        getAccountExtraDetailsRoute(location.pathname, accountData.client_id)
      );
    },
    []
  );

  const deps = [
    filteredData,
    availableHeader,
    totalHeader,
    isLoadingPortfolioData,
    isLoading,
  ];

  const emptyState =
    loaded && accounts ? (
      <AccountsEmpty
        buttonOnClick={onCreateAccountClick}
        isSearch={!!searchFilter?.length}
        hasData={!!notDeletedClientsData?.length}
        filtersSlice={portfolioFilters}
        iconWidth={isMobile ? 100 : 140}
      />
    ) : undefined;

  const accountsTable = useMemo(() => {
    return (
      <AccountsTable
        data={filteredData}
        availableHeader={availableHeader}
        totalHeader={totalHeader}
        loading={isLoading}
        accountLoading={isLoadingPortfolioData}
        getTotalAmount={getTotalAmount}
        openAccountExtraDetails={openAccountExtraDetails}
        emptyState={emptyState}
      />
    );
  }, deps);

  const accountsCardsList = useMemo(() => {
    return (
      <AccountsCardsList
        data={filteredData}
        loading={isLoading}
        accountLoading={isLoadingPortfolioData}
        openAccountExtraDetails={openAccountExtraDetails}
        emptyState={emptyState}
        renderTopContainer={renderTopContainer}
      />
    );
  }, deps);

  return (
    <>
      {isCardsView || isBottomNavigator ? accountsCardsList : accountsTable}
      {whopModal}
    </>
  );
};

export default AccountsTableContent;
